// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
const sprintf = require('sprintf-js').sprintf;

import Button from 'presentations/views/components/atoms/Button';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Avatar, {
  Group as AvatarGroup,
} from 'presentations/views/components/atoms/Avatar';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  white,
  gray01,
  gray03,
  gray05,
  black,
  gray06,
  gray02,
} from 'presentations/views/styles/color';
import {
  DateTime,
  formatToDate,
  formatToTime,
  formatToWeekDay,
} from 'infras/datetime';
import { SharedGuestUserItem } from 'presentations/api/schema/user';
import Icon from 'presentations/views/components/atoms/Icon';
import { UnavailableInfomation } from 'presentations/api/schema/calendar_event';
import { CalendarUnavailableReasonType } from 'presentations/api/schema/calendar_event/enum';
import { Transparency } from 'domains/models/event/transparency';
import CalendarUnavailableButton from '../../molecules/CalendarUnavailableButton';

interface Props {
  isLoading?: boolean;
  selectedStartDate: DateTime;
  selectedDatetime: DateTime | null;
  selectedDatetimes?: DateTime[];
  today: DateTime;
  allTimeLists: Array<Array<(any | null)[]>>;
  timeLists: Array<Array<(any | null)[]>>;
  startWeekday?: number;
  displayWeekdays?: number[];
  timeZone: string;
  color: string;
  isLightColor: boolean;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  isHostDiagnosticMode?: boolean;
  onSelect?: any;
  onClickBlockTime?: Function;
  isPoll?: boolean;
  isAvailable: boolean;
  isFetching: boolean;
  onClickCalendarUnavailableButton?: any;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const selectedTimeString: string | null = props.selectedDatetime
    ? formatToTime(props.selectedDatetime)
    : null;

  const containerRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const calendarUnavailableButtonRef = useRef<HTMLDivElement>(null);

  const isShowUnavailableButton = useMemo(() => {
    return !props.isLoading && !props.isFetching && !props.isAvailable;
  }, [props.isLoading, props.isFetching, props.isAvailable]);

  useEffect(() => {
    if (!containerRef || props.isLoading) return;

    let weekday = props.today.weekday;
    if (props.selectedDatetime) {
      weekday = props.selectedDatetime.weekday;
    }

    // 選択した日付、ない場合は今日が水曜から土曜の場合は右端にスクロールする
    if (!isShowUnavailableButton && weekday >= 3 && weekday <= 6) {
      containerRef.current?.scroll(containerRef.current.scrollWidth, 0);
    }
  }, [containerRef, props.isLoading]);

  if (isShowUnavailableButton) {
    containerRef.current?.scroll(0, 0);
  }

  const unavailableTimeListHeight = 370;
  let unavailableButtonLeftPos = 0;
  let unavailableButtonTopPos = 0;
  if (
    contentContainerRef.current &&
    contentRef.current &&
    calendarUnavailableButtonRef.current
  ) {
    const contentContainerRect = contentContainerRef.current.getBoundingClientRect();
    unavailableButtonLeftPos =
      (contentContainerRect.width -
        calendarUnavailableButtonRef.current.offsetWidth) /
      2;
    unavailableButtonTopPos = unavailableTimeListHeight / 2;
  }

  return (
    <TimeList
      ref={containerRef}
      isShowUnavailableButton={isShowUnavailableButton}
    >
      <Header>
        {props.allTimeLists.map((timeList, dayIndex) => {
          const dateTime = props.selectedStartDate.plus({ day: dayIndex });
          const isToday = dateTime.hasSame(props.today, 'day');

          const offsetDayIndex = (dayIndex + (props.startWeekday ?? 0)) % 7;
          if (
            props.displayWeekdays &&
            !props.displayWeekdays?.includes(offsetDayIndex)
          ) {
            return null;
          }

          return (
            <WeekList key={dayIndex}>
              <DayTitle
                today={isToday}
                color={props.color}
                isLightColor={props.isLightColor}
              >
                {dateTime.day}
              </DayTitle>
              <WeekDay today={isToday}>{formatToWeekDay(dateTime)}</WeekDay>
            </WeekList>
          );
        })}
      </Header>
      <ContentContainer
        isShowUnavailableButton={isShowUnavailableButton}
        unavailableTimeListHeight={unavailableTimeListHeight}
        ref={contentContainerRef}
      >
        <Content ref={contentRef}>
          {props.allTimeLists.map((timeList, dayIndex) => {
            const offsetDayIndex = (dayIndex + (props.startWeekday ?? 0)) % 7;
            if (
              props.displayWeekdays &&
              !props.displayWeekdays?.includes(offsetDayIndex)
            ) {
              return null;
            }

            const dateTime = props.selectedStartDate.plus({ day: dayIndex });
            const dateTimeString = formatToDate(dateTime);
            const dayTimeList = props.timeLists[dayIndex];
            let isSelectedDate: boolean = false;
            if (props.selectedDatetime) {
              isSelectedDate = dateTime.hasSame(props.selectedDatetime, 'day');
            }
            if (props.isLoading) {
              return (
                <WeekList key={dayIndex}>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <SkeletonItem
                      key={index}
                      isRevealGuestUser={props.isRevealGuestUser}
                    >
                      <Skeleton
                        width="100%"
                        height="100%"
                        variant="rectangular"
                      />
                    </SkeletonItem>
                  ))}
                </WeekList>
              );
            }

            return (
              <WeekList key={dayIndex}>
                {timeList.map((item, index) => {
                  const dayTime = dayTimeList.find(
                    (t) => t[0] === item[0] && t[1] === item[1],
                  );
                  const isAvailable = Boolean(dayTime);
                  const time = `${sprintf('%d', item[0])}:${sprintf(
                    '%02d',
                    item[1],
                  )}`;
                  const isFullCapacity = dayTime && dayTime[3] === 0;
                  const isShowCapacity =
                    dayTime &&
                    dayTime.length >= 4 &&
                    Number(dayTime[3]) > 0 &&
                    !props.isPoll;

                  let isSelectedDateTime =
                    isSelectedDate && time === selectedTimeString;
                  if (
                    Array.isArray(props.selectedDatetimes) &&
                    props.selectedDatetimes.length > 0
                  ) {
                    isSelectedDateTime = props.selectedDatetimes.some(
                      (selectedDatetime) => {
                        return (
                          dateTimeString ===
                            formatToDate(
                              selectedDatetime.setZone(props.timeZone),
                            ) &&
                          time ===
                            formatToTime(
                              selectedDatetime.setZone(props.timeZone),
                            )
                        );
                      },
                    );
                  }

                  const viewerEventName =
                    (dayTime ? dayTime[2]?.name : null) || null;
                  const guestUsers: SharedGuestUserItem[] | null =
                    props.isRevealGuestUser && dayTime && dayTime.length >= 5
                      ? dayTime[4]
                      : null;
                  const isTransparentEvent =
                    dayTime &&
                    dayTime[6] &&
                    dayTime[6] === Transparency.Transparent;

                  // block理由周りをitemから取得する
                  const unavailableInfomations: UnavailableInfomation[] =
                    dayTime && dayTime.length >= 6 ? dayTime[5] : [];
                  const uniqueUnavailableInfomations: UnavailableInfomation[] = [];
                  if (unavailableInfomations) {
                    for (const reason of unavailableInfomations) {
                      if (
                        uniqueUnavailableInfomations.find(
                          (uui) => uui.reason === reason.reason,
                        )
                      ) {
                        continue;
                      }
                      uniqueUnavailableInfomations.push(reason);
                    }
                  }
                  const hasUnavailableInfomation =
                    uniqueUnavailableInfomations.length > 0;

                  if (props.isHostDiagnosticMode && hasUnavailableInfomation) {
                    return (
                      <TimeListItem
                        key={time}
                        isDisabled={!isAvailable}
                        isSelected={isSelectedDateTime}
                        hasBlockReason
                        color={props.color}
                        isLightColor={props.isLightColor}
                        isRevealGuestUser={props.isRevealGuestUser}
                      >
                        <TimeButton
                          color="inherit"
                          variant="text"
                          onClick={() =>
                            props.onClickBlockTime &&
                            props.onClickBlockTime(
                              time,
                              uniqueUnavailableInfomations,
                            )
                          }
                          isDisabled={!isAvailable}
                        >
                          <TimeText>
                            {time}
                            <EventText>
                              {isAvailable && (
                                <BlockReasonIcon
                                  name="information-outline"
                                  color="inherit"
                                  className="blockReasonIcon"
                                />
                              )}
                            </EventText>
                          </TimeText>
                        </TimeButton>
                      </TimeListItem>
                    );
                  } else if (
                    props.isHostDiagnosticMode &&
                    viewerEventName &&
                    isTransparentEvent
                  ) {
                    return (
                      <TimeListItem
                        key={time}
                        color={props.color}
                        isLightColor={props.isLightColor}
                        isRevealGuestUser={props.isRevealGuestUser}
                      >
                        <TimeButton
                          color="inherit"
                          variant="text"
                          onClick={() =>
                            props.onClickBlockTime &&
                            props.onClickBlockTime(time, [
                              {
                                reason:
                                  CalendarUnavailableReasonType.Transparent,
                                users: [],
                                eventName: viewerEventName,
                              },
                            ])
                          }
                          isDisabled={!isAvailable}
                        >
                          <TimeText>
                            {time}
                            <EventText>
                              {isAvailable && (
                                <BlockReasonIcon
                                  name="information-outline"
                                  color="inherit"
                                  className="blockReasonIcon"
                                />
                              )}
                            </EventText>
                          </TimeText>
                        </TimeButton>
                      </TimeListItem>
                    );
                  } else if (viewerEventName && !isFullCapacity) {
                    return (
                      <TimeListItem
                        key={time}
                        isDisabled={!isAvailable}
                        isSelected={isSelectedDateTime}
                        hasViewerEvent={
                          props.isShowViewerCalendar && !!viewerEventName
                        }
                        color={props.color}
                        isLightColor={props.isLightColor}
                        isRevealGuestUser={props.isRevealGuestUser}
                      >
                        <TimeButton
                          color="inherit"
                          variant="text"
                          onClick={() =>
                            props.onSelect(time, dateTime, guestUsers)
                          }
                          isDisabled={!isAvailable}
                        >
                          <TimeText>
                            {time}
                            {props.isShowViewerCalendar && (
                              <EventText>{viewerEventName}</EventText>
                            )}
                            {guestUsers && guestUsers.length > 0 && (
                              <StyledAvatarGroup max={3} spacing={4}>
                                {guestUsers.map((user) => (
                                  <StyledAvatar
                                    src={user.profileImage}
                                    color={user.color}
                                  >
                                    {user.name.charAt(0)}
                                  </StyledAvatar>
                                ))}
                              </StyledAvatarGroup>
                            )}
                          </TimeText>
                        </TimeButton>
                      </TimeListItem>
                    );
                  } else if (isFullCapacity) {
                    return (
                      <TimeListItem
                        key={time}
                        isDisabled={true}
                        isSelected={false}
                        hasViewerEvent={false}
                        color={props.color}
                        isLightColor={props.isLightColor}
                        isRevealGuestUser={props.isRevealGuestUser}
                      >
                        <TimeButton
                          color="inherit"
                          variant="text"
                          isDisabled={true}
                        >
                          <TimeText>
                            {time}
                            <CapacityFullText>
                              {t('full capacity')}
                            </CapacityFullText>
                          </TimeText>
                        </TimeButton>
                      </TimeListItem>
                    );
                  } else {
                    return (
                      <TimeListItem
                        key={time}
                        isDisabled={!isAvailable}
                        isSelected={isSelectedDateTime}
                        hasViewerEvent={!!viewerEventName}
                        color={props.color}
                        isLightColor={props.isLightColor}
                        isRevealGuestUser={props.isRevealGuestUser}
                        isPoll={props.isPoll}
                      >
                        <TimeButton
                          color="inherit"
                          variant="text"
                          onClick={() =>
                            props.onSelect(time, dateTime, guestUsers)
                          }
                          isDisabled={!isAvailable}
                        >
                          <TimeText>
                            {time}
                            {dayTime && isShowCapacity && (
                              <>
                                <CapacityPCText>
                                  {t('remaining capacity pc', {
                                    count: dayTime[3],
                                  })}
                                </CapacityPCText>
                                <CapacitySPText>
                                  {t('remaining capacity sp', {
                                    count: dayTime[3],
                                  })}
                                </CapacitySPText>
                              </>
                            )}
                            {guestUsers && guestUsers.length > 0 && (
                              <StyledAvatarGroup max={3} spacing={4}>
                                {guestUsers.map((user) => (
                                  <StyledAvatar
                                    src={user.profileImage}
                                    color={user.color}
                                  >
                                    {user.name.charAt(0)}
                                  </StyledAvatar>
                                ))}
                              </StyledAvatarGroup>
                            )}
                          </TimeText>
                        </TimeButton>
                      </TimeListItem>
                    );
                  }
                })}
              </WeekList>
            );
          })}
        </Content>
      </ContentContainer>

      <CalendarUnavailableButtonLayout
        ref={calendarUnavailableButtonRef}
        isShowUnavailableButton={isShowUnavailableButton}
      >
        <CalendarUnavailableButton
          color={props.color}
          isWeeklyView={true}
          onClick={props.onClickCalendarUnavailableButton}
        />
      </CalendarUnavailableButtonLayout>
    </TimeList>
  );
});

export default Component;

const CalendarUnavailableButtonLayout = styled.div<{
  isShowUnavailableButton: boolean;
}>`
  position: absolute;
  left: calc(50% - 125.5px);
  top: calc(50% - 44px);
  ${({ isShowUnavailableButton }) => css`
    visibility: ${isShowUnavailableButton ? 'visible' : 'hidden'};
  `}
`;

const TimeList = styled.div<{
  isShowUnavailableButton: boolean;
}>`
  position: relative;
  margin-top: ${spacing(16)};
  margin-bottom: ${spacing(32)};

  ${mediaMax.tablet} {
    margin: ${spacing(16)} ${spacing(12)} ${spacing(4)};
    margin-bottom: 0;
  }
  ${mediaMax.smartphone} {
    max-height: initial;
    ${({ isShowUnavailableButton }) => css`
      overflow: ${isShowUnavailableButton ? 'hidden' : 'scroll'};
    `}
  }
`;
const Header = styled.div`
  display: flex;
`;
const ContentContainer = styled.div<{
  isShowUnavailableButton: boolean;
  unavailableTimeListHeight: number;
}>`
  ${({ isShowUnavailableButton, unavailableTimeListHeight }) =>
    isShowUnavailableButton &&
    css`
      height: ${unavailableTimeListHeight}px;
      overflow: hidden;
    `}
`;
const Content = styled.div`
  display: flex;
  position: relative;
`;

const WeekList = styled.div`
  flex: 1;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 ${spacing(8)};
  :first-of-type {
    margin-left: 0;
  }
  :last-of-type {
    margin-right: 0;
  }
  ${mediaMax.tablet} {
    margin: 0 ${spacing(3)};
  }
  ${mediaMax.smartphone} {
    flex: initial;
    flex-basis: 50px;
    min-width: 50px;
  }
`;
const DayTitle = styled.div<{
  today: boolean;
  color: string;
  isLightColor: boolean;
}>`
  width: 40px;
  height: 40px;
  line-height: 40px;
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  text-align: center;
  vertical-align: middle;
  color: ${gray01};
  margin-bottom: ${spacing(8)};

  ${(props) =>
    props.today &&
    css`
      border-radius: 50%;
      background-color: ${props.color};
      color: ${props.isLightColor ? black(props) : white(props)};
    `}

  ${mediaMax.tablet} {
    width: 20px;
    height: 20px;
    line-height: 20px;
    font-size: ${fontSize(10)};
    margin-bottom: ${spacing(4)};
  }
`;
const WeekDay = styled.div<{ today: boolean }>`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing(24)};

  ${({ today }) =>
    today &&
    css`
      font-weight: ${fontWeight('bold')};
    `}

  ${mediaMax.tablet} {
    font-size: ${fontSize(11)};
  }
`;

const SkeletonItem = styled.div<{
  isRevealGuestUser: boolean;
}>`
  width: 100%;
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(6)};
  }

  ${({ isRevealGuestUser }) => css`
    height: ${isRevealGuestUser ? '90px' : '63px'};

    ${mediaMax.tablet} {
      height: ${isRevealGuestUser ? '57px' : '36px'};
    }
  `}
`;
const TimeListItem = styled.div<{
  isDisabled?: boolean;
  isSelected?: boolean;
  hasViewerEvent?: boolean;
  hasBlockReason?: boolean;
  color: string;
  isLightColor: boolean;
  isRevealGuestUser: boolean;
  isPoll?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 63px;
  margin-bottom: ${spacing(8)};
  border-radius: ${borderRadius()};
  text-align: center;
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(6)};
  }

  ${({ isRevealGuestUser }) => css`
    height: ${isRevealGuestUser ? '90px' : '63px'};

    ${mediaMax.tablet} {
      height: ${isRevealGuestUser ? '57px' : '36px'};
    }
  `}

  ${(props) => {
    if (props.isDisabled) {
      return css`
        background-color: ${gray05(props)};
        border: none;
        color: ${gray03(props)};
      `;
    } else {
      let backgroundColor: any = white(props);
      let borderColor: any = props.color;
      let borderStyle: string = 'solid';
      let fontcolor: any = props.isLightColor ? gray01(props) : props.color;
      if (props.hasViewerEvent) {
        backgroundColor = gray05(props);
        borderColor = gray03(props);
        fontcolor = gray01(props);
      }
      if (props.hasBlockReason) {
        backgroundColor = gray06(props);
        borderColor = gray02(props);
        borderStyle = 'dashed';
        fontcolor = gray01(props);
      }
      if (props.isSelected) {
        backgroundColor = props.color;
        borderColor = props.color;
        fontcolor = props.isLightColor ? black(props) : white(props);
      }

      return css`
        background-color: ${backgroundColor};
        border: 1px ${borderStyle} ${borderColor};
        color: ${fontcolor};

        :hover {
          ${props.isPoll
            ? css`
                background-color: ${backgroundColor};
                border: 1px ${borderStyle} ${borderColor};
                color: ${fontcolor};
              `
            : css`
                background-color: ${props.color};
                border: 1px ${borderStyle} ${props.color};
                color: ${props.isLightColor ? black(props) : white(props)};
              `}
        }

        & .blockReasonIcon {
          fill: ${fontcolor};
        }
        &:hover .blockReasonIcon {
          fill: ${props.isLightColor ? black(props) : white(props)};
        }
      `;
    }
  }}

  .MuiButton-root.Mui-disabled {
    color: ${gray03};
  }
`;
const TimeButton = styled(Button)`
  width: 100%;
  height: 100%;
  min-width: initial;
  min-height: initial;
  padding: ${spacing(6)} 0;

  ${mediaMax.tablet} {
    padding: ${spacing(3)} 0;
  }
`;

const TimeText = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(18)};
  overflow: hidden;

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;
const EventText = styled.div`
  width: calc(100% - 16px);
  margin: 0 ${spacing(8)};
  font-weight: initial;
  font-size: ${fontSize(11)};
  line-height: 1em;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  ${mediaMax.tablet} {
    width: calc(100% - 8px);
    margin: 0 ${spacing(6)};
    font-size: ${fontSize(10)};
    transform: scale(0.9);
    transform-origin: 0 0;
  }
`;
const CapacityFullText = styled(EventText)`
  color: ${gray01};
`;
const CapacityPCText = styled.div`
  font-weight: initial;
  font-size: ${fontSize(12)};
  line-height: 1em;
  color: ${gray01};
  ${mediaMax.tablet} {
    display: none;
  }
`;
const CapacitySPText = styled.div`
  display: none;
  ${mediaMax.tablet} {
    display: block;
    font-weight: initial;
    width: calc(100% - 8px);
    margin: 0 ${spacing(6)};
    font-size: ${fontSize(10)};
    line-height: 1em;
  }
`;

const StyledAvatarGroup = styled(AvatarGroup)`
  justify-content: center;
  margin-top: ${spacing(8)};

  ${mediaMax.tablet} {
    margin-top: ${spacing(4)};
  }

  .MuiAvatar-colorDefault {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(10)};

    ${mediaMax.tablet} {
      width: 16px;
      height: 16px;
    }
  }
`;
const StyledAvatar = styled(Avatar)<{ color: string }>`
  width: 20px;
  height: 20px;
  font-size: ${fontSize(12)};
  font-weight: initial;

  ${({ color }) => css`
    background-color: ${color};
  `}

  ${mediaMax.tablet} {
    width: 16px;
    height: 16px;
    font-size: ${fontSize(10)};
  }
`;

const BlockReasonIcon = styled(Icon)<{ isSelected?: boolean }>`
  width: 16px !important;
  height: 16px !important;
  margin-right: ${spacing(4)};
  flex-shrink: 0;

  ${mediaMax.smartphone} {
    width: 12px !important;
    height: 12px !important;
  }
`;
